import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { useTran } from "@/hooks";
import { PATHS } from "@/lib/router";
import { useAuthStore } from "@/features/auth/hooks";
import { useCompanyStore } from "@/features/company/hooks";
import CompanySnapshotCard from "../components/CompanySnapshotCard";
import ESGOverviewCard from "../components/ESGOverviewCard";
import { PageHeader } from "@/components/common";
import { LayoutDashboard } from "lucide-react";
import NXMapCard from "../components/NXMapCard";
import NXPlanCard from "../components/NXPlanCard";
import { useOnboardingStore } from "@/features/onboarding";
import { motion } from "framer-motion";

const DashboardPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { company, companyLoading, fetchCompany } = useCompanyStore();
  const { onboardData, fetchOnboard } = useOnboardingStore();
  const { user } = useAuthStore();
  const userCompanyId = user?.companyId || null;
  const companyId =
    company?.companyId || company?._id || company?.id || userCompanyId;

  const handleRefresh = () => {
    if (companyId) {
      fetchCompany({ companyId, force: true });
    }
  };

  useEffect(() => {
    if (!userCompanyId) {
      return;
    }
    fetchCompany({ companyId: userCompanyId });
  }, [fetchCompany, userCompanyId]);

  useEffect(() => {
    fetchOnboard();
  }, [fetchOnboard]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("companyInformation.title")}
        description={t("companyInformation.description")}
        icon={LayoutDashboard}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <CompanySnapshotCard onEdit={() => navigate(PATHS.COMPANY_SETUP)} />
        <div className="grid grid-cols-1 gap-4 lg:col-span-2">
          <ESGOverviewCard
            company={company}
            loading={companyLoading}
            onRefresh={handleRefresh}
          />
          {onboardData && onboardData?.nxmap && (
            <motion.div
            // variants={itemVariants}
            >
              <NXMapCard
                baseYear={onboardData?.onboard?.baseYear}
                organizationBoundary={
                  onboardData?.onboard?.organizationBoundary
                }
                data={onboardData?.nxmap}
                onOpen={() =>
                  navigate(`${PATHS.ONBOARD}/nxmap`, {
                    state: { from: "dashboard" },
                  })
                }
              />
            </motion.div>
          )}
          {onboardData && onboardData?.nxplan && (
            <motion.div
            // variants={itemVariants}
            >
              <NXPlanCard
                baseYear={onboardData?.onboard?.baseYear}
                organizationBoundary={
                  onboardData?.onboard?.organizationBoundary
                }
                data={onboardData?.nxplan}
                onOpen={() =>
                  navigate(`${PATHS.ONBOARD}/nxplan`, {
                    state: { from: "dashboard" },
                  })
                }
              />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
});

export default DashboardPage;
